// Arena skins on the client (territory-visuals.md §14; backlog SK-2, SK-3): our own 16 procedural
// designs from the shared catalogue (@ballio/shared arena/skins.ts), drawn in Canvas2D from the
// tones of the player's palette colour (`arenaSkinTones`). Nothing here is a reference asset.
//
// - Land pattern (SK-3): a tile of `tileW × tileH` cells, its marks in ink on a transparent
//   ground, rendered once per (skin, colour, cache px) into a canvas (`SkinTileCache`, LRU of
//   PATTERN_CACHE_MAX) and filled over the owner's land path in the chunk bitmap, anchored to world
//   cell (0, 0): the chunk transform's origin is the world origin and px is a whole number, so
//   neighbouring chunks line up with no phase jump. Only the land top shows it; the side pass
//   darkens the same bitmap (a darkened pattern in the 0.33-cell side band, the cheaper option).
// - Head (`drawSkinHead`): a decoration on the block's top face, rotating with the heading; the
//   eye's pupil follows the pointer for the own head (the heading for others); the sprout stands
//   upright above the block; the star pulses.
// - Trail: the main colour or the light tone (`skinTrailColor`).
// - `drawSkinPreview`: a land swatch with the pattern and a slowly turning head (the picker).
//
// The "Hide skins" setting turns all of it off (plain blocks, flat land).

import {
  ARENA_SKIN_PULSE_MS,
  type ArenaPatternKind,
  type ArenaSkin,
  type ArenaSkinTones,
  arenaSkinById,
  arenaSkinTones,
  createRng,
  hashString,
  type Rgb,
  scaleRgb,
} from '@ballio/shared';
import { type Canvas2D, type Ctx2D, createCanvas, get2d } from '../render/canvas.js';
import { css, cssAlpha } from '../render/theme.js';
import { BLOCK_FACE, BLOCK_RADIUS, drawBlock, roundedBlock } from './block.js';
import { defaultLandPainter, type LandPainter } from './land-layer.js';
import { shadesOf } from './shades.js';

const TAU = Math.PI * 2;
/** Pattern tiles kept (per skin, colour and cache resolution). */
export const PATTERN_CACHE_MAX = 64;

interface Tones extends ArenaSkinTones {
  readonly main: Rgb;
}

function tonesOf(main: Rgb): Tones {
  return { ...arenaSkinTones(main), main };
}

/** Seeded marks (confetti, terrazzo, sprinkles): the same in every tile and every client. */
function seeded(kind: string): () => number {
  const r = createRng(hashString(kind));
  return () => r.next();
}

/** Draws `fn` at the tile and its 8 neighbours, so marks crossing an edge wrap seamlessly. */
function wrapped(ctx: Ctx2D, w: number, h: number, fn: () => void): void {
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      ctx.save();
      ctx.translate(dx * w, dy * h);
      fn();
      ctx.restore();
    }
  }
}

function star(ctx: Ctx2D, x: number, y: number, r: number, inner: number, rot: number): void {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const a = rot - Math.PI / 2 + (i * Math.PI) / 5;
    const rr = i % 2 === 0 ? r : inner;
    if (i === 0) ctx.moveTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
    else ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
  }
  ctx.closePath();
}

function flake(ctx: Ctx2D, x: number, y: number, r: number, width: number): void {
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = (i * TAU) / 6;
    const c = Math.cos(a);
    const s = Math.sin(a);
    ctx.moveTo(x, y);
    ctx.lineTo(x + c * r, y + s * r);
    // A small V on each arm at 60 % of its length.
    const bx = x + c * r * 0.6;
    const by = y + s * r * 0.6;
    for (const side of [-1, 1]) {
      const b = a + (side * Math.PI) / 4;
      ctx.moveTo(bx, by);
      ctx.lineTo(bx + Math.cos(b) * r * 0.32, by + Math.sin(b) * r * 0.32);
    }
  }
  ctx.stroke();
}

function chipShape(ctx: Ctx2D, x: number, y: number, r: number, rnd: () => number): void {
  const n = 4 + Math.floor(rnd() * 2);
  const a0 = rnd() * TAU;
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const a = a0 + (i * TAU) / n + (rnd() - 0.5) * 0.6;
    const rr = r * (0.7 + rnd() * 0.3);
    if (i === 0) ctx.moveTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
    else ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
  }
  ctx.closePath();
  ctx.fill();
}

/**
 * Paints one pattern tile in cell units (the caller scales the context so 1 = one cell): the
 * marks only, on a transparent ground. Pure drawing; every mark is ours.
 */
export function drawPatternTile(
  ctx: Ctx2D,
  kind: ArenaPatternKind,
  w: number,
  h: number,
  t: Tones,
): void {
  const ink = css(t.ink);
  ctx.fillStyle = ink;
  ctx.strokeStyle = ink;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  switch (kind) {
    case 'dots':
      for (const [x, y] of [
        [1.25, 1.25],
        [3.75, 3.75],
      ] as const) {
        ctx.beginPath();
        ctx.arc(x, y, 0.7, 0, TAU);
        ctx.fill();
      }
      return;
    case 'stripes': {
      // 45° bands 0.9 cells wide (1.27 along x), one per tile diagonal.
      const bw = 0.9 * Math.SQRT2;
      ctx.beginPath();
      for (const c of [0, w, 2 * w]) {
        ctx.moveTo(c, 0);
        ctx.lineTo(c + bw, 0);
        ctx.lineTo(c + bw - h, h);
        ctx.lineTo(c - h, h);
        ctx.closePath();
      }
      ctx.fill();
      return;
    }
    case 'checks':
      ctx.globalAlpha = 0.5;
      ctx.fillRect(0, 0, w / 2, h / 2);
      ctx.fillRect(w / 2, h / 2, w / 2, h / 2);
      ctx.globalAlpha = 1;
      return;
    case 'waves':
      ctx.lineWidth = 0.35;
      ctx.beginPath();
      for (let x = -0.5; x <= w + 0.5; x += 0.25) {
        const y = h / 2 + 0.5 * Math.sin((x / w) * TAU);
        if (x === -0.5) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      return;
    case 'chevron':
      ctx.lineWidth = 0.45;
      ctx.lineJoin = 'miter';
      ctx.beginPath();
      ctx.moveTo(-w / 2, h * 0.75);
      for (let x = 0; x <= w * 1.5; x += w / 2) {
        ctx.lineTo(x, (x / (w / 2)) % 2 === 0 ? h * 0.375 : h * 0.75);
      }
      ctx.stroke();
      return;
    case 'scales':
      ctx.lineWidth = 0.22;
      wrapped(ctx, w, h, () => {
        ctx.beginPath();
        for (const [cx, cy] of [
          [0, 0],
          [2, 0],
          [1, 1],
          [3, 1],
        ] as const) {
          ctx.moveTo(cx + 1, cy);
          ctx.arc(cx, cy, 1, 0, Math.PI);
        }
        ctx.stroke();
      });
      return;
    case 'bricks':
      ctx.fillRect(0, 0, w, 0.25);
      ctx.fillRect(0, h / 2, w, 0.25);
      ctx.fillRect(0, 0, 0.25, h / 2);
      ctx.fillRect(w / 2, h / 2, 0.25, h / 2);
      return;
    case 'confetti': {
      const rnd = seeded('confetti');
      const marks: [number, number, number, number, Rgb][] = [];
      for (let i = 0; i < 22; i++) {
        marks.push([
          rnd() * w,
          rnd() * h,
          rnd() * Math.PI,
          0.6 + rnd() * 0.4,
          i % 2 ? t.light : t.ink,
        ]);
      }
      wrapped(ctx, w, h, () => {
        for (const [x, y, a, len, c] of marks) {
          ctx.save();
          ctx.translate(x, y);
          ctx.rotate(a);
          ctx.fillStyle = css(c);
          ctx.fillRect(-len / 2, -0.13, len, 0.26);
          ctx.restore();
        }
      });
      return;
    }
    case 'stars':
      star(ctx, w * 0.25, h * 0.25, 0.8, 0.34, 0);
      ctx.fill();
      star(ctx, w * 0.75, h * 0.75, 0.8, 0.34, 0.35);
      ctx.fill();
      return;
    case 'tartan':
      ctx.globalAlpha = 0.35;
      ctx.fillRect(1, 0, 1.2, h);
      ctx.fillRect(0, 1, w, 1.2);
      ctx.globalAlpha = 0.6;
      ctx.fillRect(4, 0, 0.2, h);
      ctx.fillRect(0, 4, w, 0.2);
      ctx.globalAlpha = 1;
      return;
    case 'terrazzo': {
      const rnd = seeded('terrazzo');
      const chips: [number, number, number, Rgb, number][] = [];
      for (let i = 0; i < 10; i++) {
        chips.push([
          rnd() * w,
          rnd() * h,
          0.28 + rnd() * 0.3,
          i % 3 === 2 ? t.light : t.ink,
          hashString(`chip${i}`),
        ]);
      }
      wrapped(ctx, w, h, () => {
        for (const [x, y, r, c, seed] of chips) {
          ctx.fillStyle = css(c);
          const local = createRng(seed);
          chipShape(ctx, x, y, r, () => local.next());
        }
      });
      return;
    }
    case 'circuit':
      ctx.lineWidth = 0.2;
      ctx.beginPath();
      // A trace across the tile (continuous with its neighbours), and two with end nodes.
      ctx.moveTo(-0.1, 1.5);
      ctx.lineTo(2, 1.5);
      ctx.lineTo(3, 2.5);
      ctx.lineTo(4, 2.5);
      ctx.lineTo(5, 1.5);
      ctx.lineTo(w + 0.1, 1.5);
      ctx.moveTo(4.5, 3.6);
      ctx.lineTo(4.5, 5.4);
      ctx.moveTo(0.9, 4);
      ctx.lineTo(2.5, 4);
      ctx.lineTo(2.5, 5.2);
      ctx.stroke();
      for (const [x, y] of [
        [4.5, 3.6],
        [4.5, 5.4],
        [0.9, 4],
        [2.5, 5.2],
      ] as const) {
        ctx.fillStyle = ink;
        ctx.beginPath();
        ctx.arc(x, y, 0.3, 0, TAU);
        ctx.fill();
        ctx.fillStyle = css(t.main);
        ctx.beginPath();
        ctx.arc(x, y, 0.13, 0, TAU);
        ctx.fill();
      }
      return;
    case 'leaves':
      for (const [x, y, a] of [
        [w * 0.25, h * 0.25, -Math.PI / 6],
        [w * 0.75, h * 0.75, Math.PI / 6],
      ] as const) {
        ctx.beginPath();
        ctx.ellipse(x, y, 0.8, 0.34, a, 0, TAU);
        ctx.fill();
      }
      return;
    case 'flakes':
      flake(ctx, w * 0.25, h * 0.25, 0.75, 0.14);
      flake(ctx, w * 0.75, h * 0.75, 0.6, 0.12);
      return;
    case 'rings':
      ctx.lineWidth = 0.2;
      for (const [x, y] of [
        [w * 0.25, h * 0.25],
        [w * 0.75, h * 0.75],
      ] as const) {
        ctx.beginPath();
        ctx.arc(x, y, 0.55, 0, TAU);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(x, y, 0.17, 0, TAU);
        ctx.fill();
      }
      return;
    case 'scanlines':
      ctx.fillRect(0, h / 4, w, 0.25);
      return;
  }
}

/** Pattern tiles per (skin id, colour, px per cell), least recently used out past the cap. */
export class SkinTileCache {
  private readonly map = new Map<string, Canvas2D | null>();
  /** Tiles rendered (stats, tests). */
  rendered = 0;

  get size(): number {
    return this.map.size;
  }

  /** The tile canvas (tileW × px by tileH × px device px), or null (no pattern, no canvas). */
  tile(skin: ArenaSkin, color: Rgb, px: number): Canvas2D | null {
    const p = skin.pattern;
    if (!p) return null;
    const ipx = Math.max(1, Math.round(px));
    const key = `${skin.id}|${color}|${ipx}`;
    const hit = this.map.get(key);
    if (hit !== undefined) {
      this.map.delete(key);
      this.map.set(key, hit);
      return hit;
    }
    const canvas = this.render(p.kind, p.tileW, p.tileH, color, ipx);
    this.map.set(key, canvas);
    while (this.map.size > PATTERN_CACHE_MAX) {
      const oldest = this.map.keys().next().value as string;
      this.map.delete(oldest);
    }
    return canvas;
  }

  private render(
    kind: ArenaPatternKind,
    w: number,
    h: number,
    color: Rgb,
    px: number,
  ): Canvas2D | null {
    try {
      const canvas = createCanvas(w * px, h * px);
      const ctx = get2d(canvas);
      if (!ctx) return null;
      ctx.setTransform(px, 0, 0, px, 0, 0);
      drawPatternTile(ctx, kind, w, h, tonesOf(color));
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      this.rendered++;
      return canvas;
    } catch {
      return null;
    }
  }

  clear(): void {
    this.map.clear();
  }
}

/**
 * Fills the current path with `skin`'s pattern, world-anchored: the context's transform maps
 * world cells to device px (a × cells + e); the pattern is laid out in device px from the world
 * origin, so its phase is the same in every chunk bitmap.
 */
export function fillSkinPattern(
  ctx: Ctx2D,
  skin: ArenaSkin,
  color: Rgb,
  tiles: SkinTileCache,
): boolean {
  const m = ctx.getTransform();
  const px = Math.round(m.a);
  if (!(px >= 1)) return false;
  const tile = tiles.tile(skin, color, px);
  if (!tile) return false;
  const pattern = ctx.createPattern(tile as CanvasImageSource, 'repeat');
  if (!pattern) return false;
  ctx.save();
  // The path is already in device px; only the fill's user space changes: device px, its origin
  // at world (0, 0).
  ctx.setTransform(1, 0, 0, 1, m.e, m.f);
  ctx.fillStyle = pattern;
  ctx.fill();
  ctx.restore();
  return true;
}

/**
 * The land painter with skins (LandLayer.painter): the flat fill and seal, then the owner's
 * pattern over its land top. `enabled()` false (Hide skins) paints flat land only.
 */
export function skinLandPainter(tiles: SkinTileCache, enabled: () => boolean): LandPainter {
  return (ctx, owner) => {
    defaultLandPainter(ctx, owner);
    if (!enabled() || owner.skinId === '') return;
    const skin = arenaSkinById(owner.skinId);
    if (skin) fillSkinPattern(ctx, skin, owner.color, tiles);
  };
}

/** The trail colour for a skin (`""`, unknown ids and Hide skins: the main colour). */
export function skinTrailColor(skinId: string, color: Rgb, enabled = true): Rgb {
  if (!enabled || skinId === '') return color;
  const skin = arenaSkinById(skinId);
  return skin?.trail === 'light' ? arenaSkinTones(color).light : color;
}

/** Clips to the block's top face (unit coordinates, side 1). */
function clipFace(ctx: Ctx2D): void {
  roundedBlock(ctx, 0, 0, 1, 0, BLOCK_RADIUS);
  ctx.clip();
}

export interface SkinHeadDraw {
  /** The top face's centre (CSS px under the context transform). */
  readonly x: number;
  readonly y: number;
  /** The top face's side (px). */
  readonly face: number;
  /** The block's lift (px): the sprout's stem height scales with it. */
  readonly lift: number;
  readonly heading: number;
  /** Where a 'pointer' decoration looks (radians): the own pointer target, else the heading. */
  readonly aim: number;
  readonly color: Rgb;
  readonly nowMs: number;
  /** Per-player phase (blinks, sway), e.g. the player id. */
  readonly seed: number;
}

/** Draws `skin`'s head decoration on a block face (every mark is ours). */
export function drawSkinHead(ctx: Ctx2D, skin: ArenaSkin, d: SkinHeadDraw): void {
  const t = tonesOf(d.color);
  const deep = css(t.deep);
  const light = css(t.light);
  const u = d.face * skin.head.scale;
  ctx.save();
  ctx.translate(d.x, d.y);
  const kind = skin.head.kind;
  if (kind === 'sprout') {
    drawSprout(ctx, d, t, u);
    ctx.restore();
    return;
  }
  if (kind === 'eye') {
    drawEye(ctx, d, t, u);
    ctx.restore();
    return;
  }
  ctx.rotate(skin.head.facing === 'pointer' ? d.aim : d.heading);
  ctx.scale(u, u);
  ctx.fillStyle = deep;
  ctx.strokeStyle = deep;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  switch (kind) {
    case 'dots3':
      for (const [x, y] of [
        [0.2, 0],
        [-0.14, -0.2],
        [-0.14, 0.2],
      ] as const) {
        ctx.beginPath();
        ctx.arc(x, y, 0.1, 0, TAU);
        ctx.fill();
      }
      break;
    case 'band':
      clipFace(ctx);
      ctx.rotate(Math.PI / 4);
      ctx.fillRect(-0.8, -0.13, 1.6, 0.26);
      ctx.fillStyle = light;
      ctx.fillRect(-0.8, -0.13, 1.6, 0.05);
      break;
    case 'check':
      clipFace(ctx);
      ctx.globalAlpha = 0.6;
      ctx.fillRect(-0.5, -0.5, 0.5, 0.5);
      ctx.fillRect(0, 0, 0.5, 0.5);
      break;
    case 'wave':
      ctx.lineWidth = 0.09;
      ctx.beginPath();
      for (let k = 0; k <= 16; k++) {
        const y = -0.32 + (k / 16) * 0.64;
        const x = 0.09 * Math.sin((k / 16) * TAU * 1.5);
        if (k === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      break;
    case 'chevron':
      ctx.lineWidth = 0.12;
      ctx.beginPath();
      ctx.moveTo(-0.02, -0.25);
      ctx.lineTo(0.24, 0);
      ctx.lineTo(-0.02, 0.25);
      ctx.stroke();
      ctx.strokeStyle = light;
      ctx.lineWidth = 0.08;
      ctx.beginPath();
      ctx.moveTo(-0.26, -0.17);
      ctx.lineTo(-0.09, 0);
      ctx.lineTo(-0.26, 0.17);
      ctx.stroke();
      break;
    case 'arcs':
      clipFace(ctx);
      ctx.lineWidth = 0.07;
      ctx.beginPath();
      for (const [cx, cy] of [
        [-0.25, -0.18],
        [0.25, -0.18],
        [-0.5, 0.12],
        [0, 0.12],
        [0.5, 0.12],
      ] as const) {
        ctx.moveTo(cx + 0.25, cy);
        ctx.arc(cx, cy, 0.25, 0, Math.PI);
      }
      ctx.stroke();
      break;
    case 'mortar':
      clipFace(ctx);
      ctx.lineWidth = 0.1;
      ctx.lineCap = 'butt';
      ctx.beginPath();
      ctx.moveTo(-0.6, 0);
      ctx.lineTo(0.6, 0);
      ctx.moveTo(0, 0);
      ctx.lineTo(0, -0.6);
      ctx.moveTo(-0.22, 0);
      ctx.lineTo(-0.22, 0.6);
      ctx.stroke();
      break;
    case 'sprinkles': {
      const marks = [
        [0.22, -0.12, 0.4, t.deep],
        [-0.05, 0.22, 1.3, t.light],
        [-0.22, -0.18, 2.2, 0xffffff],
        [0.12, 0.18, 2.8, t.deep],
        [-0.2, 0.05, 0.9, t.deep],
        [0.02, -0.25, 1.9, t.light],
      ] as const;
      for (const [x, y, a, c] of marks) {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(a);
        ctx.fillStyle = css(c);
        ctx.fillRect(-0.09, -0.035, 0.18, 0.07);
        ctx.restore();
      }
      break;
    }
    case 'star': {
      const ph = ((d.nowMs + d.seed * 97) % ARENA_SKIN_PULSE_MS) / ARENA_SKIN_PULSE_MS;
      const k = skin.head.pulse ? 1 - 0.15 * (0.5 - 0.5 * Math.cos(ph * TAU)) : 1;
      star(ctx, 0, 0, 0.32 * k, 0.14 * k, Math.PI / 2);
      ctx.fill();
      break;
    }
    case 'plus':
      ctx.fillRect(-0.3, -0.075, 0.6, 0.15);
      ctx.fillRect(-0.075, -0.3, 0.15, 0.6);
      ctx.fillStyle = light;
      ctx.fillRect(-0.075, -0.075, 0.15, 0.15);
      break;
    case 'chips': {
      const rnd = seeded('chips-head');
      for (const [x, y, r, c] of [
        [0.15, -0.14, 0.12, t.deep],
        [-0.16, 0.02, 0.1, t.light],
        [0.08, 0.2, 0.09, t.deep],
      ] as const) {
        ctx.fillStyle = css(c);
        chipShape(ctx, x, y, r, rnd);
      }
      break;
    }
    case 'chip':
      ctx.lineWidth = 0.05;
      ctx.lineCap = 'butt';
      ctx.beginPath();
      for (let i = -1; i <= 1; i++) {
        const o = i * 0.11;
        ctx.moveTo(o, -0.3);
        ctx.lineTo(o, 0.3);
        ctx.moveTo(-0.3, o);
        ctx.lineTo(0.3, o);
      }
      ctx.stroke();
      ctx.fillRect(-0.2, -0.2, 0.4, 0.4);
      ctx.fillStyle = light;
      ctx.beginPath();
      ctx.arc(0.1, -0.1, 0.045, 0, TAU);
      ctx.fill();
      break;
    case 'flake':
      flake(ctx, 0, 0, 0.3, 0.06);
      break;
    case 'visor':
      clipFace(ctx);
      ctx.fillStyle = css(scaleRgb(d.color, 0.28));
      ctx.fillRect(0.06, -0.6, 0.6, 1.2);
      ctx.strokeStyle = cssAlpha(0xffffff, 0.6);
      ctx.lineWidth = 0.05;
      ctx.beginPath();
      ctx.moveTo(0.17, -0.3);
      ctx.lineTo(0.17, -0.06);
      ctx.stroke();
      break;
  }
  ctx.restore();
}

/** One big eye, upright; the pupil looks along `aim`; a 120 ms blink every ~4 s. */
function drawEye(ctx: Ctx2D, d: SkinHeadDraw, t: Tones, u: number): void {
  const r = 0.31 * u;
  const period = 4000 + (d.seed % 7) * 230;
  const into = (d.nowMs + d.seed * 613) % period;
  const open = into < 120 ? Math.abs(into / 60 - 1) : 1;
  ctx.save();
  ctx.scale(1, Math.max(0.08, open));
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, TAU);
  ctx.fill();
  ctx.lineWidth = Math.max(1, 0.045 * u);
  ctx.strokeStyle = css(t.deep);
  ctx.stroke();
  const px = Math.cos(d.aim) * 0.12 * u;
  const py = Math.sin(d.aim) * 0.12 * u;
  ctx.fillStyle = '#1b1b1f';
  ctx.beginPath();
  ctx.arc(px, py, 0.135 * u, 0, TAU);
  ctx.fill();
  ctx.fillStyle = cssAlpha(0xffffff, 0.85);
  ctx.beginPath();
  ctx.arc(px - 0.05 * u, py - 0.05 * u, 0.04 * u, 0, TAU);
  ctx.fill();
  ctx.restore();
}

/** A two-leaf sprout standing up from the face (level > 1), swaying gently. */
function drawSprout(ctx: Ctx2D, d: SkinHeadDraw, t: Tones, u: number): void {
  const sway = Math.sin(d.nowMs / 650 + d.seed) * 0.12;
  const stem = 0.38 * u + d.lift * 0.3;
  ctx.rotate(sway);
  ctx.strokeStyle = css(t.deep);
  ctx.lineWidth = Math.max(1, 0.07 * u);
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.quadraticCurveTo(0.06 * u, -stem * 0.5, 0, -stem);
  ctx.stroke();
  for (const side of [-1, 1]) {
    ctx.save();
    ctx.translate(0, -stem);
    ctx.rotate(side * 0.9);
    ctx.fillStyle = css(side < 0 ? t.light : t.deep);
    ctx.beginPath();
    ctx.ellipse(0, -0.17 * u, 0.09 * u, 0.18 * u, 0, 0, TAU);
    ctx.fill();
    ctx.restore();
  }
}

export interface SkinPreview {
  /** CSS px. */
  readonly w: number;
  readonly h: number;
  /** Device px per CSS px. */
  readonly dpr: number;
  readonly skinId: string;
  readonly color: Rgb;
  readonly nowMs: number;
  /** CSS px per cell (default: the swatch spans about 7 cells). */
  readonly cellPx?: number;
}

/**
 * The picker's live preview: a round land swatch (side band, top, the pattern anchored to the
 * swatch) and the head block in the middle, turning slowly. Clears the canvas first.
 */
export function drawSkinPreview(ctx: Ctx2D, tiles: SkinTileCache, o: SkinPreview): void {
  const { w, h, dpr } = o;
  const skin = arenaSkinById(o.skinId);
  const sh = shadesOf(o.color);
  const cell = o.cellPx ?? Math.min(w, h) / 7;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, Math.ceil(w * dpr), Math.ceil(h * dpr));
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(w, h) * 0.42;
  const lift = cell * 0.33;
  ctx.fillStyle = css(sh.side);
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, TAU);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy - lift, r, 0, TAU);
  ctx.fillStyle = css(sh.main);
  ctx.fill();
  if (skin) {
    // World-anchored like the land: the device transform scaled to cells for the pattern.
    ctx.save();
    ctx.setTransform(cell * dpr, 0, 0, cell * dpr, 0, 0);
    fillSkinPattern(ctx, skin, o.color, tiles);
    ctx.restore();
  }
  const side = cell * 1.9;
  const heading = (o.nowMs / 2600) % TAU;
  const y = drawBlock(ctx, cx, cy, side, lift * 1.4, heading, sh);
  if (skin) {
    drawSkinHead(ctx, skin, {
      x: cx,
      y,
      face: side * BLOCK_FACE,
      lift: lift * 1.4,
      heading,
      aim: heading + Math.sin(o.nowMs / 900) * 0.9,
      color: o.color,
      nowMs: o.nowMs,
      seed: 3,
    });
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}
